// MF-554 — The school's Boards: status, who sees them (★ = opens by default for that role), and the
// actions. A new Board starts as a draft with just a name, then opens in the editor.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faClone,
  faFileExport,
  faFileImport,
  faListCheck,
  faSliders
} from '@fortawesome/pro-light-svg-icons'
import useFetchData from '../../../../../hooks/useFetchData'
import {
  cloneBoard,
  createBoard,
  deleteBoard,
  exportBoard,
  getBoards,
  importBoard,
  publishBoard,
  unpublishBoard
} from '../../../../../services/boards/boards.service'
import { SUMMARY_PALETTE } from '../boardTheme'
import BuilderTabs from './BuilderTabs'
import LibraryAvailabilityDialog from './LibraryAvailabilityDialog'
import PatSettingsDialog from './PatSettingsDialog'

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''

const BoardsList = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { data: roles } = useFetchData('roles')
  const roleName = useMemo(() => new Map((roles || []).map((r) => [String(r._id), r.name])), [roles])
  const [boards, setBoards] = useState(null)
  const [busy, setBusy] = useState(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [toDelete, setToDelete] = useState(null)
  // MF-577 — curators (Maestro, or a role with the BoardsCurator module) choose what the school offers
  const isCurator = useSelector(
    (state) =>
      Boolean(state?.session?.permissions?.boardscurator?.updated) ||
      (state?.session?.profile?.roles || []).some((r) => (r.id || r.name) === 'Maestro')
  )
  const [availabilityOpen, setAvailabilityOpen] = useState(false)
  const [patSettingsOpen, setPatSettingsOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const importInput = useRef(null)
  const [importing, setImporting] = useState(null) // { fileName, payload, error } while the import dialog is open

  const load = useCallback(() => {
    getBoards({ limit: 200 })
      .then((d) => setBoards(d?.paginatedResults || []))
      .catch((err) => {
        setBoards([])
        enqueueSnackbar(err.message || 'Boards could not be loaded', { variant: 'error' })
      })
  }, [enqueueSnackbar])
  useEffect(load, [load])

  const run = async (id, fn, ok) => {
    setBusy(id)
    try {
      const res = await fn()
      enqueueSnackbar(ok, { variant: 'success' })
      const noData = res?.publishNotice?.noData
      if (noData?.length)
        enqueueSnackbar(
          `${noData.length} tile${noData.length === 1 ? ' has' : 's have'} no data in this school and stay hidden until it arrives: ${noData.map((n) => n.split(':')[0]).join(', ')}`,
          { variant: 'info', autoHideDuration: 10000 }
        )
      load()
    } catch (err) {
      enqueueSnackbar(err.message || 'Something went wrong', { variant: 'error', autoHideDuration: 8000 })
    } finally {
      setBusy(null)
    }
  }

  // Clone: a draft copy to reuse a Board with a small change; it opens straight away in the editor.
  const clone = async (b) => {
    setBusy(b._id)
    try {
      const copy = await cloneBoard(b._id)
      enqueueSnackbar(`Cloned as “${copy.name}” (draft)`, { variant: 'success' })
      navigate(String(copy._id))
    } catch (err) {
      enqueueSnackbar(err.message || 'The board could not be cloned', { variant: 'error' })
    } finally {
      setBusy(null)
    }
  }

  // Export: the Board as a file to take to another school (or environment). No data travels, only layout.
  const exportFile = async (b) => {
    setBusy(b._id)
    try {
      const file = await exportBoard(b._id)
      const url = URL.createObjectURL(new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `${b.code || 'board'}.maestro-board.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      enqueueSnackbar(err.message || 'The board could not be exported', { variant: 'error' })
    } finally {
      setBusy(null)
    }
  }

  const pickFile = (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    f.text()
      .then((text) => setImporting({ fileName: f.name, payload: JSON.parse(text), error: null }))
      .catch(() => setImporting({ fileName: f.name, payload: null, error: 'This file is not valid JSON.' }))
  }

  const doImport = async () => {
    setBusy('import')
    try {
      const { board, warnings } = await importBoard(importing.payload)
      setImporting(null)
      enqueueSnackbar(
        warnings?.length
          ? `Imported as a draft. ${warnings.join(' ')}`
          : `Imported “${board.name}” as a draft`,
        {
          variant: warnings?.length ? 'warning' : 'success',
          autoHideDuration: warnings?.length ? 12000 : 5000
        }
      )
      navigate(String(board._id))
    } catch (err) {
      setImporting((cur) => ({ ...cur, error: err.message || 'The board could not be imported' }))
    } finally {
      setBusy(null)
    }
  }

  const create = async () => {
    setBusy('new')
    try {
      const board = await createBoard({ name: newName.trim() })
      setCreating(false)
      setNewName('')
      navigate(String(board._id))
    } catch (err) {
      enqueueSnackbar(err.message || 'The board could not be created', { variant: 'error' })
    } finally {
      setBusy(null)
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: SUMMARY_PALETTE.ink }}>
            Board Builder
          </Typography>
          <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted }}>
            Build boards from the Chart Library. A published board appears as a button in Analytics for the
            roles you choose.
          </Typography>
        </Box>
        {/* The secondary actions live in one menu so the header stays short on small screens and can grow. */}
        <Button
          variant='outlined'
          aria-haspopup='menu'
          aria-expanded={menuAnchor ? 'true' : undefined}
          endIcon={<FontAwesomeIcon icon={faChevronDown} />}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{ mr: 1 }}
        >
          More
        </Button>
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null)
              importInput.current?.click()
            }}
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={faFileImport} />
            </ListItemIcon>
            Import board
          </MenuItem>
          {isCurator
            ? [
                <MenuItem
                  key='availability'
                  onClick={() => {
                    setMenuAnchor(null)
                    setAvailabilityOpen(true)
                  }}
                >
                  <ListItemIcon>
                    <FontAwesomeIcon icon={faListCheck} />
                  </ListItemIcon>
                  Library availability
                </MenuItem>,
                <MenuItem
                  key='pat'
                  onClick={() => {
                    setMenuAnchor(null)
                    setPatSettingsOpen(true)
                  }}
                >
                  <ListItemIcon>
                    <FontAwesomeIcon icon={faSliders} />
                  </ListItemIcon>
                  PAT settings
                </MenuItem>
              ]
            : null}
        </Menu>
        <input ref={importInput} type='file' accept='application/json,.json' hidden onChange={pickFile} />
        <Button
          variant='contained'
          startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}
          onClick={() => setCreating(true)}
        >
          New board
        </Button>
      </Box>
      <BuilderTabs />
      <LibraryAvailabilityDialog open={availabilityOpen} onClose={() => setAvailabilityOpen(false)} />
      <PatSettingsDialog open={patSettingsOpen} onClose={() => setPatSettingsOpen(false)} />

      <Paper variant='outlined' sx={{ borderRadius: '14px', overflow: 'hidden' }}>
        {boards === null ? (
          <Box sx={{ p: 2 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={48} />
            ))}
          </Box>
        ) : boards.length === 0 ? (
          <Typography sx={{ p: 4, color: SUMMARY_PALETTE.muted, textAlign: 'center' }}>
            No boards yet. Create one to start.
          </Typography>
        ) : (
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Board</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Visible to</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align='right'>
                  Tiles
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align='right'>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {boards.map((b) => {
                const published = b.status === 'published'
                // MF-576 — only the creator, Maestro or whoever manages the school's Boards may change a Board
                const mine = b.canManage !== false
                const notMine =
                  'Only the person who created this board, or whoever manages the school’s boards, can change it. You can clone it.'
                const defaults = new Set((b.audience?.defaultForRole || []).map(String))
                return (
                  <TableRow key={b._id} hover>
                    <TableCell sx={{ cursor: 'pointer' }} onClick={() => navigate(String(b._id))}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{b.name}</Typography>
                      {b.description ? (
                        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted }} noWrap>
                          {b.description}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size='small'
                        label={published ? 'Published' : 'Draft'}
                        color={published ? 'success' : 'default'}
                        variant={published ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(b.audience?.accessRole || []).length === 0 ? (
                          <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.faint }}>
                            Maestro only
                          </Typography>
                        ) : (
                          b.audience.accessRole.map((id) => (
                            <Chip
                              key={id}
                              size='small'
                              color={defaults.has(String(id)) ? 'primary' : 'default'}
                              label={`${defaults.has(String(id)) ? '★ ' : ''}${roleName.get(String(id)) || 'Role'}`}
                            />
                          ))
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align='right'>{b.tiles?.length || 0}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted }}>
                      {fmtDate(b.updatedAt)}
                      {b.updatedBy?.fullName ? ` · ${b.updatedBy.fullName}` : ''}
                    </TableCell>
                    <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={mine ? 'Edit' : notMine}>
                        <span>
                          <IconButton size='small' disabled={!mine} onClick={() => navigate(String(b._id))}>
                            <FontAwesomeIcon icon={['fal', 'pencil']} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title='Clone (a draft copy)'>
                        <span>
                          <IconButton size='small' disabled={busy === b._id} onClick={() => clone(b)}>
                            <FontAwesomeIcon icon={faClone} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title='Export to a file (layout only, no data)'>
                        <span>
                          <IconButton size='small' disabled={busy === b._id} onClick={() => exportFile(b)}>
                            <FontAwesomeIcon icon={faFileExport} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          !mine ? notMine : published ? 'Unpublish (removes the Analytics button)' : 'Publish'
                        }
                      >
                        <span>
                          <IconButton
                            size='small'
                            disabled={!mine || busy === b._id}
                            onClick={() =>
                              run(
                                b._id,
                                () => (published ? unpublishBoard(b._id) : publishBoard(b._id)),
                                published ? 'Board unpublished' : 'Board published'
                              )
                            }
                          >
                            <FontAwesomeIcon icon={['fal', published ? 'eye-slash' : 'upload']} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={mine ? 'Delete' : notMine}>
                        <span>
                          <IconButton
                            size='small'
                            disabled={!mine || busy === b._id}
                            onClick={() => setToDelete(b)}
                          >
                            <FontAwesomeIcon icon={['fal', 'trash']} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={creating} onClose={() => setCreating(false)} maxWidth='xs' fullWidth>
        <DialogTitle>New board</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin='dense'
            label='Name'
            value={newName}
            inputProps={{ maxLength: 80 }}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && newName.trim() && create()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)}>Cancel</Button>
          <Button variant='contained' disabled={!newName.trim() || busy === 'new'} onClick={create}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(importing)} onClose={() => setImporting(null)} maxWidth='sm' fullWidth>
        <DialogTitle>Import board</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            <b>{importing?.fileName}</b>
          </Typography>
          {importing?.payload?.board ? (
            <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, mt: 1 }}>
              “{importing.payload.board.name}” · {importing.payload.board.tiles?.length || 0} tiles. It is
              created as a draft: nothing is published and no role opens it by default. Roles are matched by
              name; the Library items must exist here.
            </Typography>
          ) : null}
          {importing?.error ? (
            <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.bad, mt: 1.5 }}>
              {importing.error}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImporting(null)}>Cancel</Button>
          <Button variant='contained' disabled={!importing?.payload || busy === 'import'} onClick={doImport}>
            Import
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(toDelete)} onClose={() => setToDelete(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Delete board</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <b>{toDelete?.name}</b>?
            {toDelete?.status === 'published' ? ' Its Analytics button is removed too.' : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)}>Cancel</Button>
          <Button
            color='error'
            variant='contained'
            onClick={() => {
              const b = toDelete
              setToDelete(null)
              run(b._id, () => deleteBoard(b._id), 'Board deleted')
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default BoardsList
